import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Divider, useTheme } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Decimation,
} from "chart.js";
import annotationPlugin, { AnnotationOptions } from "chartjs-plugin-annotation";
import zoomPlugin from "chartjs-plugin-zoom";
import ChartTitle from "./components/ChatTitle";
import ChartActions from "./components/ChartActions";
import ExpandButton from "./components/ExpandButton";
import ZoomButton from "./components/ZoomButton";
import ResetButton from "./components/ResetButton";
import StatsSummary from "./components/StatsSummary";
import Typography from "../Typography";
import {
  getChartHeight,
  getColors,
  getDatasets,
  getNoDataBoxes,
  getThresholdAnnotations,
  getTimestampDiffInMinutes,
  getTooltipTitle,
  xTicksCallback,
} from "./utils";
import {
  getDefaultOptions,
  DEFAULT_ZOOM_LEVEL,
  STEP_SIZE,
  CHART_HEIGHT,
  DEFAULT_Y_MAX,
} from "./const";
import { GraphType, MonitoringGraphData } from "@services/monitoring/types";
import { ZoomOptions } from "chartjs-plugin-zoom/types/options";
import { LineChartData, LineChartInstance } from "./types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Decimation,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
  zoomPlugin
);

export type LineChartProps = {
  title: string;
  unitMeasurement: string;
  chartData: MonitoringGraphData[];
  annotations?: Record<string, AnnotationOptions>;
  color?: "pink" | "purple" | "blue" | "teal" | "green" | "mustard";
  height?: number;
  yMax?: number;
  //request start/end time
  start?: number;
  end?: number;
  chartType?: GraphType;
  onRefresh?: () => void;
  onToggle?: (chartType: GraphType) => void;
};

const LineChart = (props: LineChartProps) => {
  const {
    title,
    unitMeasurement,
    chartData,
    annotations,
    color,
    height = CHART_HEIGHT,
    yMax,
    start,
    end,
    chartType,
    onRefresh,
    onToggle,
  } = props;

  const theme = useTheme();
  const { t } = useTranslation();
  const chartRef = useRef<LineChartInstance>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL);

  const chartHeight = getChartHeight(height, isExpanded);
  const colors = useMemo(() => getColors(color, theme), [color]);
  const mainColor = colors[0];
  const showLegend = chartData.length > 1;
  const isZoomed = zoomLevel !== DEFAULT_ZOOM_LEVEL;

  const data: LineChartData = useMemo(
    () => ({
      datasets: getDatasets(chartData, colors),
    }),
    [chartData, colors]
  );

  // need to create instance only once
  const options = useMemo(
    () => getDefaultOptions(unitMeasurement),
    [unitMeasurement, chartType]
  );

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    chart.options.scales!.x!.ticks!.callback = xTicksCallback(start);
    chart.options.plugins!.tooltip!.callbacks!.title = getTooltipTitle(start);
  }, [start, chartType]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart || !start || !end) return;

    const suggestedMax = getTimestampDiffInMinutes(end, start);

    chart.options.scales!.x!.suggestedMax = suggestedMax;
    chart.update();
  }, [start, end, chartType]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    chart.options.scales!.y!.max = yMax || DEFAULT_Y_MAX;
    chart.update();
  }, [yMax, chartType]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    chart.options.plugins!.legend!.display = showLegend;
    chart.update();
  }, [showLegend, chartType]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    chart.options.plugins!.annotation!.annotations = {
      ...getNoDataBoxes(chartData),
      ...getThresholdAnnotations(chartData, theme),
      ...annotations,
    };
    chart.update();
  }, [chartData]);

  const onZoom: NonNullable<ZoomOptions["onZoom"]> = useCallback(
    ({ chart }) => {
      setZoomLevel(chart.getZoomLevel());
    },
    [chartType]
  );

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    chart.options.plugins!.zoom!.zoom!.onZoom = onZoom;
  }, [onZoom]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) return;

    const currentTicks = chart.options.scales!.x!.ticks;
    const stepSize = !isZoomed ? STEP_SIZE : undefined;

    chart.options.scales!.x!.ticks = { ...currentTicks, stepSize };
    chart.update();
  }, [isZoomed]);

  useEffect(() => {
    chartRef.current?.resetZoom();
    setZoomLevel(1);
  }, [isExpanded, chartType]);

  return (
    <Stack
      sx={{
        width: "100%",
        position: "relative",
        border: `2px solid ${mainColor}`,
        borderRadius: 2,
        gap: 1
      }}>
      <ChartTitle title={title} unit={unitMeasurement} bgColor={mainColor} />
      <ChartActions
        chartType={chartType}
        onRefresh={onRefresh}
        onToggle={onToggle}
      />
      <Stack
        sx={{
          padding: 2,
          paddingTop: 0,
          gap: 1
        }}>
        <Box
          sx={{
            height: chartHeight,
            transition: "height 0.3s ease-in-out",
          }}>
          <Line ref={chartRef} data={data} options={options} />
        </Box>

        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1
            }}>
            <ExpandButton
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
            />
            <ResetButton chartRef={chartRef} setZoomLevel={setZoomLevel} />
            <ZoomButton type="zoomIn" chartRef={chartRef} />
            <ZoomButton type="zoomOut" chartRef={chartRef} />
            <Divider orientation="vertical" variant="middle" flexItem />
            <Typography size="medium">
              {t("Zoom Level: {{zoomLevel}}", { zoomLevel })}
            </Typography>
          </Stack>

          <StatsSummary
            datasets={data.datasets}
            colors={colors}
            unit={unitMeasurement}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default LineChart;
